import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Inbox, CheckCircle2, X, MessageSquare, Calendar, AlertTriangle, FileText, ChevronRight, Receipt, Plane } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BUCKET_META = {
  pending_claims: { label: 'Expense Claims', icon: Receipt, color: 'bg-amber-500' },
  pending_leaves: { label: 'Leave Requests', icon: Plane, color: 'bg-blue-500' },
  disputed_payslips: { label: 'Disputed Payslips', icon: MessageSquare, color: 'bg-rose-500' },
  overdue_compliance: { label: 'Overdue Compliance', icon: AlertTriangle, color: 'bg-rose-600' },
  overdue_hr_documents: { label: 'Overdue HR Documents', icon: FileText, color: 'bg-orange-500' },
};

export default function ManagerInboxPage() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('pending_claims');
  const [loading, setLoading] = useState(true);
  const [selectedClaims, setSelectedClaims] = useState(new Set());
  const [selectedLeaves, setSelectedLeaves] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  // Confirmation dialog state — works for both single + bulk decisions
  const [confirm, setConfirm] = useState(null); // { kind, decision, ids?, id?, label }
  const [confirmNote, setConfirmNote] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const navigate = useNavigate();

  const fetchInbox = async () => {
    try {
      const r = await api.get('/manager/inbox');
      setData(r.data);
      setSelectedClaims(new Set());
      setSelectedLeaves(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load inbox');
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchInbox(); }, []);

  // ----- Confirmation flow -----
  const openConfirm = (cfg) => { setConfirmNote(''); setConfirm(cfg); };
  const closeConfirm = () => { setConfirm(null); setConfirmNote(''); };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmLoading(true);
    const note = confirmNote.trim();
    try {
      if (confirm.kind === 'claim') {
        await api.post(`/expense-claims/${confirm.id}/decide`, { decision: confirm.decision, note });
        toast.success(`Claim ${confirm.decision}`);
      } else if (confirm.kind === 'leave') {
        await api.post(`/leaves/${confirm.id}/decide`, { decision: confirm.decision, note });
        toast.success(`Leave ${confirm.decision}`);
      } else if (confirm.kind === 'bulk') {
        const r = await api.post('/manager/inbox/bulk-decide', {
          claim_ids: confirm.claim_ids || [],
          leave_ids: confirm.leave_ids || [],
          decision: confirm.decision,
          note,
        });
        toast.success(`${r.data.claims_updated} claim(s) + ${r.data.leaves_updated} leave(s) ${confirm.decision}`);
      }
      closeConfirm();
      fetchInbox();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setConfirmLoading(false); }
  };

  const decideClaim = (id, decision, employee = '') => openConfirm({
    kind: 'claim', id, decision,
    title: `${decision === 'approved' ? 'Approve' : 'Reject'} expense claim`,
    body: `${employee ? `From ${employee}. ` : ''}This action will be logged in the activity feed.`,
  });

  const decideLeave = (id, decision, employee = '') => openConfirm({
    kind: 'leave', id, decision,
    title: `${decision === 'approved' ? 'Approve' : 'Reject'} leave request`,
    body: `${employee ? `From ${employee}. ` : ''}This action will be logged in the activity feed.`,
  });

  const bulkDecide = (decision) => {
    const claim_ids = Array.from(selectedClaims);
    const leave_ids = Array.from(selectedLeaves);
    if (claim_ids.length === 0 && leave_ids.length === 0) {
      toast.error('Select at least one item');
      return;
    }
    setBulkLoading(false);
    openConfirm({
      kind: 'bulk', decision, claim_ids, leave_ids,
      title: `${decision === 'approved' ? 'Approve' : 'Reject'} ${claim_ids.length + leave_ids.length} item(s)`,
      body: `${claim_ids.length} claim(s) + ${leave_ids.length} leave(s) will be ${decision}. Already-decided rows are skipped automatically.`,
    });
  };

  const toggleClaim = (id) => {
    setSelectedClaims(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleLeave = (id) => {
    setSelectedLeaves(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllClaims = (checked) => {
    if (checked) setSelectedClaims(new Set((data?.pending_claims || []).map(c => c.id)));
    else setSelectedClaims(new Set());
  };
  const toggleAllLeaves = (checked) => {
    if (checked) setSelectedLeaves(new Set((data?.pending_leaves || []).map(l => l.id)));
    else setSelectedLeaves(new Set());
  };

  if (loading) {
    return <DashboardLayout><div className="max-w-6xl mx-auto"><div className="h-32 animate-pulse bg-muted rounded" /></div></DashboardLayout>;
  }
  if (!data) return <DashboardLayout><div className="max-w-6xl mx-auto p-6">Failed to load</div></DashboardLayout>;

  const totalUrgent = (data.counts?.disputed_payslips || 0) + (data.counts?.overdue_compliance || 0) + (data.counts?.overdue_hr_documents || 0);

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl mx-auto min-w-0" data-testid="manager-inbox-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-700 flex items-center justify-center shadow-lg">
            <Inbox size={22} className="text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Manager Inbox</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {data.counts.total === 0
                ? 'All clear — no items need your attention'
                : <><strong>{data.counts.total}</strong> item(s) waiting · <strong className="text-rose-600">{totalUrgent}</strong> urgent</>}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchInbox} data-testid="refresh-inbox">Refresh</Button>
        </div>

        {/* Quick stat tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {Object.keys(BUCKET_META).map(k => {
            const meta = BUCKET_META[k];
            const Icon = meta.icon;
            const count = data.counts[k] || 0;
            return (
              <Card key={k} className={`cursor-pointer border-2 transition-all ${tab === k ? 'ring-2 ring-offset-1 ring-foreground' : ''} ${count > 0 ? 'border-foreground/20' : 'opacity-60'}`}
                onClick={() => setTab(k)} data-testid={`inbox-tile-${k}`}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.color}`}>
                      <Icon size={14} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-2xl font-bold font-outfit text-foreground">{count}</div>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground truncate">{meta.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Tabs body */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 w-full h-auto">
            {Object.keys(BUCKET_META).map(k => (
              <TabsTrigger key={k} value={k} className="text-xs whitespace-normal h-auto py-2">{BUCKET_META[k].label}</TabsTrigger>
            ))}
          </TabsList>

          {/* Expense claims */}
          <TabsContent value="pending_claims">
            {selectedClaims.size > 0 && (
              <div className="mb-2 flex flex-col sm:flex-row gap-2 sm:items-center justify-between p-2 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800" data-testid="bulk-bar-claims">
                <span className="text-sm font-medium"><strong>{selectedClaims.size}</strong> claim(s) selected</span>
                <div className="inline-flex gap-2">
                  <Button size="sm" disabled={bulkLoading} onClick={() => bulkDecide('approved')}
                    className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bulk-approve-claims">
                    <CheckCircle2 size={12} className="mr-1" />Bulk Approve
                  </Button>
                  <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => bulkDecide('rejected')}
                    className="h-8 text-rose-600 border-rose-300" data-testid="bulk-reject-claims">
                    <X size={12} className="mr-1" />Bulk Reject
                  </Button>
                </div>
              </div>
            )}
            <Card className="w-full overflow-hidden"><CardContent className="p-0"><div className="w-full min-w-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 w-8">
                      <Checkbox
                        checked={data.pending_claims.length > 0 && selectedClaims.size === data.pending_claims.length}
                        onCheckedChange={toggleAllClaims}
                        data-testid="select-all-claims"
                      />
                    </th>
                    <th className="text-left px-4 py-2">Employee</th>
                    <th className="text-right px-4 py-2">Amount</th>
                    <th className="text-left px-4 py-2">Category</th>
                    <th className="text-left px-4 py-2 hidden md:table-cell">Description</th>
                    <th className="text-left px-4 py-2 hidden sm:table-cell">Submitted</th>
                    <th className="text-right px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.pending_claims.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No pending expense claims</td></tr>
                  ) : data.pending_claims.map(c => (
                    <tr key={c.id} className="border-b border-border" data-testid={`claim-row-${c.id}`}>
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={selectedClaims.has(c.id)}
                          onCheckedChange={() => toggleClaim(c.id)}
                          data-testid={`select-claim-${c.id}`}
                        />
                      </td>
                      <td className="px-4 py-2 font-medium">{c.employee_name}</td>
                      <td className="px-4 py-2 text-right font-mono font-bold">{c.currency} {Number(c.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="px-4 py-2 capitalize text-xs">{c.category}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground max-w-[300px] truncate hidden md:table-cell" title={c.description}>{c.description}</td>
                      <td className="px-4 py-2 font-mono text-xs hidden sm:table-cell">{(c.created_at || '').slice(0, 10)}</td>
                      <td className="px-4 py-2 text-right">
                        <div className="inline-flex gap-1 flex-wrap justify-end">
                          {c.receipt_url && (
                            <button onClick={async () => {
                              try {
                                const r = await api.get(`/expense-claims/${c.id}/receipt`, { responseType: 'blob' });
                                const url = URL.createObjectURL(r.data);
                                window.open(url, '_blank');
                                setTimeout(() => URL.revokeObjectURL(url), 60000);
                              } catch { toast.error('Receipt download failed'); }
                            }} className="px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs">Receipt</button>
                          )}
                          <Button size="sm" onClick={() => decideClaim(c.id, 'approved', c.employee_name)}
                            className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-claim-${c.id}`}>
                            <CheckCircle2 size={11} className="mr-1" />Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => decideClaim(c.id, 'rejected', c.employee_name)}
                            className="h-7 px-2 text-xs text-rose-600 border-rose-300" data-testid={`reject-claim-${c.id}`}>
                            <X size={11} className="mr-1" />Reject
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div></CardContent></Card>
          </TabsContent>

          {/* Leave requests */}
          <TabsContent value="pending_leaves">
            {selectedLeaves.size > 0 && (
              <div className="mb-2 flex flex-col sm:flex-row gap-2 sm:items-center justify-between p-2 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800" data-testid="bulk-bar-leaves">
                <span className="text-sm font-medium"><strong>{selectedLeaves.size}</strong> leave(s) selected</span>
                <div className="inline-flex gap-2">
                  <Button size="sm" disabled={bulkLoading} onClick={() => bulkDecide('approved')}
                    className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bulk-approve-leaves">
                    <CheckCircle2 size={12} className="mr-1" />Bulk Approve
                  </Button>
                  <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => bulkDecide('rejected')}
                    className="h-8 text-rose-600 border-rose-300" data-testid="bulk-reject-leaves">
                    <X size={12} className="mr-1" />Bulk Reject
                  </Button>
                </div>
              </div>
            )}
            <Card className="w-full overflow-hidden"><CardContent className="p-0"><div className="w-full min-w-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 w-8">
                      <Checkbox
                        checked={data.pending_leaves.length > 0 && selectedLeaves.size === data.pending_leaves.length}
                        onCheckedChange={toggleAllLeaves}
                        data-testid="select-all-leaves"
                      />
                    </th>
                    <th className="text-left px-4 py-2">Employee</th>
                    <th className="text-left px-4 py-2">Type</th>
                    <th className="text-left px-4 py-2">Dates</th>
                    <th className="text-right px-4 py-2">Days</th>
                    <th className="text-left px-4 py-2 hidden md:table-cell">Reason</th>
                    <th className="text-right px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.pending_leaves.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No pending leaves</td></tr>
                  ) : data.pending_leaves.map(l => (
                    <tr key={l.id} className="border-b border-border" data-testid={`leave-row-${l.id}`}>
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={selectedLeaves.has(l.id)}
                          onCheckedChange={() => toggleLeave(l.id)}
                          data-testid={`select-leave-${l.id}`}
                        />
                      </td>
                      <td className="px-4 py-2 font-medium">{l.employee_name}</td>
                      <td className="px-4 py-2 capitalize text-xs">{l.leave_type}</td>
                      <td className="px-4 py-2 font-mono text-xs">{l.start_date?.slice(0, 10)} → {l.end_date?.slice(0, 10)}</td>
                      <td className="px-4 py-2 text-right font-mono font-bold">{l.days_count}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground max-w-[280px] truncate hidden md:table-cell" title={l.reason}>{l.reason || '—'}</td>
                      <td className="px-4 py-2 text-right">
                        <div className="inline-flex gap-1 flex-wrap justify-end">
                          <Button size="sm" onClick={() => decideLeave(l.id, 'approved', l.employee_name)}
                            className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-leave-${l.id}`}>
                            <CheckCircle2 size={11} className="mr-1" />Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => decideLeave(l.id, 'rejected', l.employee_name)}
                            className="h-7 px-2 text-xs text-rose-600 border-rose-300" data-testid={`reject-leave-${l.id}`}>
                            <X size={11} className="mr-1" />Reject
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div></CardContent></Card>
          </TabsContent>

          {/* Disputed payslips */}
          <TabsContent value="disputed_payslips">
            <Card className="w-full overflow-hidden"><CardContent className="p-0"><div className="w-full min-w-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">Employee</th>
                    <th className="text-left px-4 py-2">Period</th>
                    <th className="text-right px-4 py-2">Net Pay</th>
                    <th className="text-left px-4 py-2">Disputed</th>
                    <th className="text-left px-4 py-2">Reason</th>
                    <th className="text-right px-4 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.disputed_payslips.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No disputed payslips</td></tr>
                  ) : data.disputed_payslips.map(p => (
                    <tr key={p.id} className="border-b border-border" data-testid={`dispute-row-${p.id}`}>
                      <td className="px-4 py-2 font-medium">{p.employee_name || p.employee_id?.slice(0, 8)}</td>
                      <td className="px-4 py-2 font-mono text-xs">{p.period}</td>
                      <td className="px-4 py-2 text-right font-mono font-bold">{p.currency || 'SAR'} {Number(p.net_pay || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="px-4 py-2 font-mono text-xs">{(p.dispute_at || '').slice(0, 10)}</td>
                      <td className="px-4 py-2 text-xs text-rose-600 max-w-[300px] truncate" title={p.dispute_reason}>{p.dispute_reason}</td>
                      <td className="px-4 py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/payroll?run_id=${p.payroll_run_id || ''}`)} className="h-7 px-2 text-xs">
                          Open <ChevronRight size={11} className="ml-1" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div></CardContent></Card>
          </TabsContent>

          {/* Overdue compliance */}
          <TabsContent value="overdue_compliance">
            <Card className="w-full overflow-hidden"><CardContent className="p-0"><div className="w-full min-w-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">Item</th>
                    <th className="text-left px-4 py-2">Authority</th>
                    <th className="text-left px-4 py-2">Expired</th>
                    <th className="text-right px-4 py-2">Days Overdue</th>
                    <th className="text-right px-4 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.overdue_compliance.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No overdue compliance items</td></tr>
                  ) : data.overdue_compliance.map(r => (
                    <tr key={r.id} className="border-b border-border">
                      <td className="px-4 py-2 font-medium">{r.title}</td>
                      <td className="px-4 py-2 text-xs">{r.issuing_authority || '—'}</td>
                      <td className="px-4 py-2 font-mono text-xs">{r.expiry_date}</td>
                      <td className="px-4 py-2 text-right font-mono font-bold text-rose-600">{r.days_overdue}d</td>
                      <td className="px-4 py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => navigate('/compliance')} className="h-7 px-2 text-xs">Open <ChevronRight size={11} className="ml-1" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div></CardContent></Card>
          </TabsContent>

          {/* Overdue HR documents */}
          <TabsContent value="overdue_hr_documents">
            <Card className="w-full overflow-hidden"><CardContent className="p-0"><div className="w-full min-w-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">Employee</th>
                    <th className="text-left px-4 py-2">Doc Type</th>
                    <th className="text-left px-4 py-2">Expired</th>
                    <th className="text-right px-4 py-2">Days Overdue</th>
                    <th className="text-right px-4 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.overdue_hr_documents.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No overdue HR documents</td></tr>
                  ) : data.overdue_hr_documents.map(r => (
                    <tr key={r.id} className="border-b border-border">
                      <td className="px-4 py-2 font-medium">{r.employee_name}</td>
                      <td className="px-4 py-2 capitalize text-xs">{(r.document_type || '').replace(/_/g, ' ')}</td>
                      <td className="px-4 py-2 font-mono text-xs">{r.expiry_date}</td>
                      <td className="px-4 py-2 text-right font-mono font-bold text-rose-600">{r.days_overdue}d</td>
                      <td className="px-4 py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => navigate('/hr-documents')} className="h-7 px-2 text-xs">Open <ChevronRight size={11} className="ml-1" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div></CardContent></Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Confirmation dialog (replaces window.confirm/prompt) */}
      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) closeConfirm(); }}>
        <DialogContent data-testid="confirm-decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">{confirm?.title}</DialogTitle>
            {confirm?.body && <DialogDescription>{confirm.body}</DialogDescription>}
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">
              {confirm?.decision === 'rejected' ? 'Rejection note (recommended)' : 'Note (optional)'}
            </label>
            <Textarea
              value={confirmNote}
              onChange={(e) => setConfirmNote(e.target.value)}
              rows={3}
              placeholder={confirm?.decision === 'rejected' ? 'e.g. Receipt is unclear, please resubmit.' : 'Optional note for the audit log.'}
              data-testid="confirm-decision-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeConfirm} disabled={confirmLoading} data-testid="confirm-decision-cancel">
              Cancel
            </Button>
            <Button
              onClick={runConfirm}
              disabled={confirmLoading}
              className={confirm?.decision === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-rose-600 hover:bg-rose-700 text-white'}
              data-testid="confirm-decision-submit"
            >
              {confirmLoading ? 'Working…' : (confirm?.decision === 'approved' ? 'Approve' : 'Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
